import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IdeaAccessibility,
  IdeaCategoryId,
  IdeaOrder,
  IdeasQueryParams,
  IdeaStatusId,
  useGetIdeasQuery,
  useGetOrganizationsSelectQuery,
  useGetUsersQuery,
} from '../api/api';
import { Input, ISelectOptionProp, Select } from '../components/Form';
import {
  ACCESSIBILITY_OPTIONS,
  CATEGORY_LABELS,
  CATEGORY_OPTIONS,
  ORDER_OPTIONS,
  PAGE_SIZE,
  STATUS_COLORS,
  STATUS_LABELS,
  STATUS_OPTIONS,
  formatCriticality,
  formatDateTime,
  getErrorMessage,
} from './ideas/shared';

const formatPersonName = (firstName: string, lastName: string, email: string) => {
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || email;
};

export const Ideas: FC = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [organizationFilter, setOrganizationFilter] = useState('');
  const [authorFilter, setAuthorFilter] = useState('');
  const [accessibilityFilter, setAccessibilityFilter] = useState('');
  const [order, setOrder] = useState<IdeaOrder>('CREATED_DESC');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const queryParams = useMemo<IdeasQueryParams>(() => {
    const params: IdeasQueryParams = {
      page,
      limit: PAGE_SIZE,
      order,
    };

    if (search) {
      params.search = search;
    }
    if (statusFilter) {
      params.status_id = Number(statusFilter) as IdeaStatusId;
    }
    if (categoryFilter) {
      params.category_id = categoryFilter as IdeaCategoryId;
    }
    if (organizationFilter) {
      params.organization_id = organizationFilter;
    }
    if (authorFilter) {
      params.user_id = authorFilter;
    }
    if (accessibilityFilter) {
      params.accessibility_id = accessibilityFilter as IdeaAccessibility;
    }

    return params;
  }, [accessibilityFilter, authorFilter, categoryFilter, order, organizationFilter, page, search, statusFilter]);

  const { data: ideasResponse, isLoading, isFetching, error } = useGetIdeasQuery(queryParams);
  const { data: organizations } = useGetOrganizationsSelectQuery();
  const { data: users } = useGetUsersQuery();

  const organizationNames = useMemo(
    () => new Map((organizations ?? []).map((organization) => [organization._id, organization.name])),
    [organizations]
  );

  const authorNames = useMemo(
    () =>
      new Map(
        (users ?? []).map((user) => [
          user.id,
          formatPersonName(user.first_name, user.last_name, user.email),
        ])
      ),
    [users]
  );

  const authorOptions = useMemo<ISelectOptionProp[]>(
    () =>
      [{ _id: '', name: 'Все авторы' }].concat(
        (users ?? [])
          .map((user) => ({
            _id: user.id,
            name: `${formatPersonName(user.first_name, user.last_name, user.email)} · ${user.organization_name}`,
          }))
          .sort((left, right) => left.name.localeCompare(right.name))
      ),
    [users]
  );

  const organizationFilterOptions = useMemo<ISelectOptionProp[]>(
    () => [{ _id: '', name: 'Все организации' }].concat(organizations ?? []),
    [organizations]
  );

  const statusFilterOptions = useMemo<ISelectOptionProp[]>(
    () => [{ _id: '', name: 'Все статусы' }, ...STATUS_OPTIONS],
    []
  );

  const categoryFilterOptions = useMemo<ISelectOptionProp[]>(
    () => [{ _id: '', name: 'Все категории' }, ...CATEGORY_OPTIONS],
    []
  );

  const accessibilityFilterOptions = useMemo<ISelectOptionProp[]>(
    () => [{ _id: '', name: 'Любая доступность' }, ...ACCESSIBILITY_OPTIONS],
    []
  );

  const total = ideasResponse?.total ?? 0;
  const currentPage = ideasResponse?.page ?? page;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fromItem = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const toItem = total === 0 ? 0 : Math.min(currentPage * PAGE_SIZE, total);

  const handleFilterChange = (setter: (value: string) => void, value: string) => {
    setPage(1);
    setter(value);
  };

  const listErrorMessage = error ? getErrorMessage(error) : null;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Идеи</h1>
          <p className="mt-1 text-sm text-gray-500">
            Просмотр списка идей портала. Открой идею, чтобы увидеть шапку и голоса пользователей.
          </p>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Input
          label="Поиск"
          placeholder="Поиск по названию, описанию, заметке..."
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />
        <Select
          label="Статус"
          value={statusFilter}
          onChange={(event) => handleFilterChange(setStatusFilter, event.target.value)}
          options={statusFilterOptions}
          showEmptyOption={false}
        />
        <Select
          label="Категория"
          value={categoryFilter}
          onChange={(event) => handleFilterChange(setCategoryFilter, event.target.value)}
          options={categoryFilterOptions}
          showEmptyOption={false}
        />
        <Select
          label="Доступность"
          value={accessibilityFilter}
          onChange={(event) => handleFilterChange(setAccessibilityFilter, event.target.value)}
          options={accessibilityFilterOptions}
          showEmptyOption={false}
        />
        <Select
          label="Организация"
          value={organizationFilter}
          onChange={(event) => handleFilterChange(setOrganizationFilter, event.target.value)}
          options={organizationFilterOptions}
          showEmptyOption={false}
        />
        <Select
          label="Автор"
          value={authorFilter}
          onChange={(event) => handleFilterChange(setAuthorFilter, event.target.value)}
          options={authorOptions}
          showEmptyOption={false}
        />
        <Select
          label="Сортировка"
          value={order}
          onChange={(event) => {
            setPage(1);
            setOrder(event.target.value as IdeaOrder);
          }}
          options={ORDER_OPTIONS}
          showEmptyOption={false}
        />
      </div>

      {listErrorMessage ? (
        <div className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {listErrorMessage}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg bg-white shadow-md">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="text-sm text-gray-500">
            {isFetching ? 'Обновляем список...' : `Показано ${fromItem}-${toItem} из ${total}`}
          </div>
          <div className="text-sm text-gray-500">Страница {currentPage} из {totalPages}</div>
        </div>

        {isLoading ? (
          <div className="px-6 py-10 text-center text-gray-500">Загружаем идеи...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[1500px] table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-[140px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Создана</th>
                  <th className="w-[360px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Название</th>
                  <th className="w-[190px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Организация</th>
                  <th className="w-[190px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Автор</th>
                  <th className="w-[90px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Голоса</th>
                  <th className="w-[150px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Критичность</th>
                  <th className="w-[90px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Затраты</th>
                  <th className="w-[170px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Категория</th>
                  <th className="w-[150px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Статус</th>
                  <th className="w-[120px] px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Доступность</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {ideasResponse?.items.length ? (
                  ideasResponse.items.map((idea) => (
                    <tr
                      key={idea.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => navigate(`/ideas/${idea.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap align-top text-sm text-gray-700">{formatDateTime(idea.created_at)}</td>
                      <td className="w-[360px] max-w-[360px] px-6 py-4 align-top text-sm text-gray-900">
                        <div className="break-words font-medium">{idea.title}</div>
                        <div
                          className="mt-1 overflow-hidden break-words whitespace-normal text-xs text-gray-500"
                          style={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 5,
                          }}
                        >
                          {idea.description}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-normal break-words align-top text-sm text-gray-700">
                        {organizationNames.get(idea.organization_id) ?? idea.organization_id}
                      </td>
                      <td className="px-6 py-4 whitespace-normal break-words align-top text-sm text-gray-700">
                        {authorNames.get(idea.user_id) ?? idea.user_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap align-top text-sm text-gray-700">{idea.votes_count ?? 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap align-top text-sm text-gray-700">
                        {formatCriticality(idea.avg_criticality)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap align-top text-sm text-gray-700">
                        {idea.costs === null ? '—' : idea.costs}
                      </td>
                      <td className="px-6 py-4 whitespace-normal break-words align-top text-sm text-gray-700">
                        {idea.category_id ? CATEGORY_LABELS[idea.category_id] : 'Без категории'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap align-top text-sm">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_COLORS[idea.status_id]}`}>
                          {STATUS_LABELS[idea.status_id]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap align-top text-sm text-gray-700">
                        {idea.accessibility_id === 'public' ? 'Публичная' : 'Приватная'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="px-6 py-10 text-center text-sm text-gray-500">
                      По текущим фильтрам идеи не найдены.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
          <div className="text-sm text-gray-500">
            {total === 0 ? 'Нет результатов' : `Показано ${fromItem}-${toItem} из ${total} идей`}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              className="cursor-pointer rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:bg-gray-300"
              disabled={currentPage <= 1 || isFetching}
            >
              Назад
            </button>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-blue-300"
              disabled={currentPage >= totalPages || isFetching}
            >
              Вперед
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
